import React, { Suspense } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { PDFViewer } from '@/components/pdf-viewer/pdf-viewer';
import { FileActionBar, downloadFile } from './file-action-bar';

const LazyXlsxViewer = React.lazy(() =>
  import('@/components/xlsx-viewer/xlsx-viewer').then((m) => ({ default: m.XlsxViewer }))
);

interface FileViewerProps {
  fileUrl: string;
  fileName: string;
  onDelete?: (() => void) | null;
  withCredentials?: boolean;
  className?: string;
  transcript?: string;
  contentType?: string;
}

function isPdf(fileName: string): boolean {
  return fileName.toLowerCase().endsWith('.pdf');
}

function isPreviewableImage(fileName: string): boolean {
  const ext = fileName.toLowerCase();
  return (
    ext.endsWith('.png') ||
    ext.endsWith('.jpg') ||
    ext.endsWith('.jpeg') ||
    ext.endsWith('.gif') ||
    ext.endsWith('.webp')
  );
}

function isSpreadsheet(fileName: string): boolean {
  const ext = fileName.toLowerCase();
  return ext.endsWith('.xlsx') || ext.endsWith('.xls') || ext.endsWith('.csv');
}

function isAudioContentType(contentType: string | undefined): boolean {
  return (contentType ?? '').startsWith('audio/');
}

export function FileViewer({
  fileUrl,
  fileName,
  onDelete = null,
  withCredentials = false,
  className,
  transcript,
  contentType,
}: FileViewerProps): React.ReactElement {
  const handleDownload = (): void => downloadFile(fileUrl, fileName);

  if (isAudioContentType(contentType)) {
    return (
      <div className={`flex flex-col ${className ?? 'h-full'}`}>
        <FileActionBar onDownload={handleDownload} onDelete={onDelete ?? null} />
        <div className="flex flex-col items-center gap-6 flex-1 min-h-0 p-6 overflow-auto">
          <audio
            controls
            preload="none"
            crossOrigin={withCredentials ? 'use-credentials' : undefined}
            className="w-full max-w-md"
          >
            <source src={fileUrl} type={contentType} />
          </audio>
          {transcript && (
            <div className="w-full max-w-2xl">
              <h3 className="text-sm font-medium mb-2">Transcript</h3>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground leading-relaxed">
                {transcript}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isPdf(fileName)) {
    return (
      <PDFViewer
        fileUrl={fileUrl}
        className={className ?? 'h-full'}
        withCredentials={withCredentials}
        onDownload={handleDownload}
        onDelete={onDelete ?? null}
      />
    );
  }

  if (isPreviewableImage(fileName)) {
    return (
      <div className={`flex flex-col ${className ?? 'h-full'}`}>
        <FileActionBar onDownload={handleDownload} onDelete={onDelete ?? null} />
        <div className="flex items-center justify-center flex-1 min-h-0 p-4 overflow-auto">
          <img
            src={fileUrl}
            alt={fileName}
            className="max-w-full max-h-full object-contain"
            crossOrigin={withCredentials ? 'use-credentials' : undefined}
          />
        </div>
      </div>
    );
  }

  if (isSpreadsheet(fileName)) {
    return (
      <Suspense
        fallback={
          <div className={`flex flex-col ${className ?? 'h-full'}`}>
            <FileActionBar onDownload={handleDownload} onDelete={onDelete ?? null} />
            <div className="flex items-center justify-center flex-1">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          </div>
        }
      >
        <LazyXlsxViewer
          fileUrl={fileUrl}
          fileName={fileName}
          className={className ?? 'h-full'}
          withCredentials={withCredentials}
          onDownload={handleDownload}
          onDelete={onDelete ?? null}
        />
      </Suspense>
    );
  }

  return (
    <div className={`flex flex-col ${className ?? 'h-full'}`}>
      <FileActionBar onDownload={handleDownload} onDelete={onDelete ?? null} />
      <div className="flex flex-col items-center justify-center flex-1 gap-3 text-muted-foreground p-4">
        <FileText size={40} className="text-muted-foreground/50" />
        <p className="text-sm">{fileName}</p>
        <p className="text-xs">Preview not available for this file type</p>
      </div>
    </div>
  );
}
